import { Injectable } from '@angular/core';
import {
  DATE_PIPE_WITH_COMMA, DATE_PIPE_WITH_COMMA_12H, DATE_TIME_FORMAT_12H, DATE_TIME_FORMAT_24H,
  MOMENT_DATE_TIME_FORMAT_12H, MOMENT_TIME_FORMAT_12H, TIME_FORMAT_12H, TIME_FORMAT_24H
} from '../constants/date.constants';
import { WIDGET_TIME_FORMAT, WidgetTimeFormat } from '../db-models/widget-conf-dao';

/**
 * Service for date and time formatting utilities
 * Detects and caches user's preferred time format based on browser locale or partner setting
 */
@Injectable({
  providedIn: 'root'
})
export class DateUtilsService {

  private _userTimeFormat: Record<string, string> | null = null;
  private _userDateTimeFormat: Record<string, string> | null = null;
  private _userDateTimeWithCommaFormat: string | null = null;
  private _widgetTimeFormat: WidgetTimeFormat = WIDGET_TIME_FORMAT.AUTO;

  constructor() {
    // Initialize format detection on service creation
    this.detectUserTimeFormat();
  }

  /**
   * Gets the cached user time format (detects on first call)
   * @returns Moment.js format string: 'hh:mm A' for 12-hour or 'HH:mm' for 24-hour
   */
  get userTimeFormat(): Record<string, string> {
    if (this._userTimeFormat === null) {
      this.detectUserTimeFormat();
    }
    return this._userTimeFormat;
  }

  /**
   * Gets the cached user date-time format (detects on first call)
   * @returns Moment.js format string for date and time combined
   */
  get userDateTimeFormat(): Record<string, string> {
    if (this._userDateTimeFormat === null) {
      this.detectUserTimeFormat();
    }
    return this._userDateTimeFormat;
  }

  /**
   * Gets the cached date-time format with comma separator (detects on first call)
   * @returns Angular DatePipe format string: 'dd.MM.yyyy, hh:mm a' for 12-hour or 'dd.MM.yyyy, HH:mm' for 24-hour
   */
  get userDateTimeWithCommaFormat(): string {
    if (this._userDateTimeWithCommaFormat === null) {
      this.detectUserTimeFormat();
    }
    return this._userDateTimeWithCommaFormat;
  }

  /**
   * Detects user's preferred time format based on partner setting or browser's Intl API
   * This method is called once during service initialization and can be re-called when partner data loads
   * @private
   */
  private detectUserTimeFormat(): void {
    try {
      let has12HourFormat: boolean;

      if (this._widgetTimeFormat === WIDGET_TIME_FORMAT.TWELVE_HOURS) {
        has12HourFormat = true;
      } else if (this._widgetTimeFormat === WIDGET_TIME_FORMAT.TWENTY_FOUR_HOURS) {
        has12HourFormat = false;
      } else {
        // 'auto' mode: detect from browser locale
        const formattedTime = new Intl.DateTimeFormat(navigator.language || 'de-CH', {
          hour: "numeric",
          minute: "numeric"
        }).format(new Date(2025, 0, 1, 13, 0));

        // Check if the formatted time contains AM/PM markers
        // This works across different locales (AM/PM, am/pm, a.m./p.m., etc.)
        has12HourFormat = /am|pm|a\.m\.|p\.m\./i.test(formattedTime) || formattedTime.includes('午後') || formattedTime.includes('午前');
      }

      // Set the cached format values
      this._userTimeFormat = has12HourFormat ? { date: TIME_FORMAT_12H, moment: MOMENT_TIME_FORMAT_12H } : { date: TIME_FORMAT_24H, moment: TIME_FORMAT_24H };
      this._userDateTimeFormat = has12HourFormat ? { date: DATE_TIME_FORMAT_12H, moment: MOMENT_DATE_TIME_FORMAT_12H } : { date: DATE_TIME_FORMAT_24H, moment: DATE_TIME_FORMAT_24H };
      this._userDateTimeWithCommaFormat = has12HourFormat ? DATE_PIPE_WITH_COMMA_12H : DATE_PIPE_WITH_COMMA;
    } catch (error) {
      // Fallback to 24-hour format if detection fails
      console.warn('Failed to detect time format, defaulting to 24-hour format', error);
      this._userTimeFormat = { date: TIME_FORMAT_24H, moment: TIME_FORMAT_24H };
      this._userDateTimeFormat = { date: DATE_TIME_FORMAT_24H, moment: DATE_TIME_FORMAT_24H };
      this._userDateTimeWithCommaFormat = DATE_PIPE_WITH_COMMA;
    }
  }

  /**
   * Forces re-detection of the time format (useful if locale changes dynamically)
   */
  refreshTimeFormat(): void {
    this.detectUserTimeFormat();
  }

  /**
   * Sets the widget time format preference from partner settings
   * @param format - The partner's widget_time_format setting (AUTO, TWELVE_HOURS, TWENTY_FOUR_HOURS)
   */
  setWidgetTimeFormat(format: WidgetTimeFormat): void {
    this._widgetTimeFormat = format || WIDGET_TIME_FORMAT.AUTO;
    this.detectUserTimeFormat();
  }
}
